import React, { createContext, useContext, useMemo } from 'react'

import {
  getSubscriptionLoading,
  getSubscriptionScheduledCancellationAt,
} from '@disco/state-management/selectors/subscription'
import { getUser } from '@disco/state-management/selectors/user'
import dayjs from 'dayjs'
import useLocalStorage from 'hooks/use-local-storage'
import { useState } from 'react'
import { useSelector } from 'react-redux'

const defaultContextValue = {
  shouldShowBanner: false,
  daysRemainingUntilCancellation: null as number | null,
  setScheduledCancellationBannerDismissedAt: (date: Date) => {},
}

const CancellationBannerContext = createContext(defaultContextValue)

export function CancellationBannerProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const user = useSelector(getUser)
  const isLoading = useSelector(getSubscriptionLoading)
  const cancelledAt = useSelector(getSubscriptionScheduledCancellationAt)

  const [
    localStorageScheduledCancellationBannerDismissedAt,
    setLocalStorageScheduledCancellationBannerDismissedAt,
  ] = useLocalStorage<Date | null>(
    'scheduled_cancellation_banner_dismissed_at',
    null,
  )

  const [
    scheduledCancellationBannerDismissedAt,
    setScheduledCancellationBannerDismissedAt,
  ] = useState<Date | null>(localStorageScheduledCancellationBannerDismissedAt)

  let shouldShowBanner = false
  let daysRemainingUntilCancellation: number | null = null

  if (!isLoading && user.loggedIn && cancelledAt) {
    const today = dayjs(new Date())

    const subscriptionScheduledCancellationAt = dayjs(cancelledAt)

    daysRemainingUntilCancellation = subscriptionScheduledCancellationAt.diff(
      today,
      'day',
    )

    shouldShowBanner = daysRemainingUntilCancellation <= 28

    if (scheduledCancellationBannerDismissedAt) {
      shouldShowBanner = false

      const scheduledCancellationBannerDismissedAtDate = dayjs(
        scheduledCancellationBannerDismissedAt,
      )

      const datesToShowBanner = [
        subscriptionScheduledCancellationAt.subtract(28, 'days'),
        subscriptionScheduledCancellationAt.subtract(21, 'days'),
        subscriptionScheduledCancellationAt.subtract(14, 'days'),
        subscriptionScheduledCancellationAt.subtract(7, 'days'),
        subscriptionScheduledCancellationAt.subtract(3, 'days'),
        subscriptionScheduledCancellationAt.subtract(2, 'days'),
        subscriptionScheduledCancellationAt.subtract(1, 'days'),
      ]

      datesToShowBanner.forEach(dateToShowBanner => {
        const todayIsEqualToOrAfterDateToShowBanner =
          today.isSame(dateToShowBanner, 'day') ||
          today.isAfter(dateToShowBanner, 'day')

        if (
          todayIsEqualToOrAfterDateToShowBanner &&
          scheduledCancellationBannerDismissedAtDate.isBefore(
            dateToShowBanner,
            'day',
          )
        ) {
          shouldShowBanner = true
        }
      })
    }
  }

  const value = useMemo(
    () => ({
      shouldShowBanner,
      daysRemainingUntilCancellation,
      setScheduledCancellationBannerDismissedAt: (date: Date) => {
        setLocalStorageScheduledCancellationBannerDismissedAt(date)
        setScheduledCancellationBannerDismissedAt(date)
      },
    }),
    [
      shouldShowBanner,
      daysRemainingUntilCancellation,
      setLocalStorageScheduledCancellationBannerDismissedAt,
      setScheduledCancellationBannerDismissedAt,
    ],
  )

  return (
    <CancellationBannerContext.Provider value={value}>
      {children}
    </CancellationBannerContext.Provider>
  )
}

export function useCancellationBanner() {
  return useContext(CancellationBannerContext)
}
